import { Flow, Text } from 'bold-ui'
import { DateTime } from 'components/date'
import { CustomSelectFieldProps, SelectField } from 'components/form'
import { css } from 'emotion'
import { LoteImunobiologicoSelectFieldDocument } from 'graphql/hooks.generated'
import {
  ImunobiologicoFabricante,
  LoteImunobiologicoSelectFieldQuery,
  LoteImunobiologicoSelectFieldQueryVariables,
} from 'graphql/types.generated'
import moment from 'moment'
import React from 'react'

import { DEFAULT_SELECT_SIZE, useAsyncQuerySelect } from '../useAsyncQuerySelect'

export type LoteImunobiologicoSelectModel = NonNullable<
  NonNullable<LoteImunobiologicoSelectFieldQuery['lotesImunobiologico']>['content'][number]
>

type FabricanteLabelModel = Partial<Pick<ImunobiologicoFabricante, 'codigoRnds' | 'nome' | 'sigla'>> | null

export type LoteImunobiologicoSelectFieldProps = CustomSelectFieldProps<LoteImunobiologicoSelectModel> & {
  mostrarInativos?: boolean
  imunobiologicoId?: ID
}

const extractItems = (data: LoteImunobiologicoSelectFieldQuery): ReadonlyArray<LoteImunobiologicoSelectModel> => {
  return (data?.lotesImunobiologico?.content ?? []) as ReadonlyArray<LoteImunobiologicoSelectModel>
}

const handleItemToString = (item: LoteImunobiologicoSelectModel) => getLoteImunobiologicoSelectFieldItemText(item)

const renderItem = (item: LoteImunobiologicoSelectModel) => {
  return (
    item && (
      <Flow direction='vertical' gap={0}>
        <Flow direction='horizontal' gap={0.5}>
          <Text
            style={css`
              word-break: break-all;
            `}
          >
            <strong>{`Lote: ${item.lote ?? '-'}`}</strong>
          </Text>
        </Flow>
        <Flow direction='horizontal' gap={0.5}>
          <Text>
            <strong>Fabricante: </strong>
            {getLoteImunobiologicoFabricanteExpandedLabel(item.fabricante)}
          </Text>
        </Flow>
        <Flow direction='horizontal' gap={0.5}>
          <strong>Validade:</strong>
          <DateTime value={item.validade ?? undefined} format='DD/MM/YYYY' />
        </Flow>
      </Flow>
    )
  )
}

export function LoteImunobiologicoSelectField(props: LoteImunobiologicoSelectFieldProps) {
  const { mostrarInativos, imunobiologicoId, ...rest } = props
  const variables = (input: string): LoteImunobiologicoSelectFieldQueryVariables => ({
    input: {
      filtroImunoFabricante: input,
      mostrarLotesInativos: !!mostrarInativos,
      imunobiologicoId: imunobiologicoId,
      pageParams: {
        size: DEFAULT_SELECT_SIZE,
      },
    },
  })

  const { selectProps } = useAsyncQuerySelect<
    LoteImunobiologicoSelectModel,
    LoteImunobiologicoSelectFieldQuery,
    LoteImunobiologicoSelectFieldQueryVariables
  >({
    query: LoteImunobiologicoSelectFieldDocument,
    extractItems: extractItems,
    variables,
  })

  return (
    <SelectField<LoteImunobiologicoSelectModel>
      renderItem={renderItem}
      itemToString={handleItemToString}
      {...selectProps}
      {...rest}
    />
  )
}

const getNonBlankValue = (value?: string | null) => value?.trim() || undefined

const formatWithSeparator = (...values: Array<string | undefined>) => values.filter(Boolean).join(' - ')

export const getLoteImunobiologicoFabricanteLabel = (fabricante?: FabricanteLabelModel) => {
  const codigoRnds = getNonBlankValue(fabricante?.codigoRnds)
  const sigla = getNonBlankValue(fabricante?.sigla)
  const nome = getNonBlankValue(fabricante?.nome)

  if (!codigoRnds) {
    return sigla ?? nome ?? '-'
  }

  return formatWithSeparator(codigoRnds, sigla ?? nome) || codigoRnds
}

export const getLoteImunobiologicoFabricanteExpandedLabel = (fabricante?: FabricanteLabelModel) => {
  const codigoRnds = getNonBlankValue(fabricante?.codigoRnds)
  const sigla = getNonBlankValue(fabricante?.sigla)
  const nome = getNonBlankValue(fabricante?.nome)

  if (!codigoRnds) {
    return sigla ?? nome ?? '-'
  }

  if (sigla && nome) {
    return formatWithSeparator(codigoRnds, sigla, nome)
  }

  return formatWithSeparator(codigoRnds, sigla ?? nome) || codigoRnds
}

export const getLoteImunobiologicoSelectFieldItemText = (item: LoteImunobiologicoSelectModel) => {
  if (!item) return ''

  const lote = item.lote ?? '-'
  const fabricante = getLoteImunobiologicoFabricanteExpandedLabel(item.fabricante)
  const validade = item.validade ? moment(item.validade).format('DD/MM/YYYY') : '-'

  return `Lote: ${lote} | Fabricante: ${fabricante} | Validade: ${validade}`
}
